/**
 * case-http — live HTTP implementation of :class:`CaseListPort`.
 *
 * Wraps two BE reads, both **snake_case** on the wire:
 *   - `GET /api/v1/cases` → `PaginatedResponse{items, total}` (the picker)
 *   - `GET /api/v1/case-ecritures/by-case/{caseId}` → array of briefs
 *
 * Hand-rolled validation (no zod / no codegen) per the auth-http / search-http
 * precedent. W1/W2: pure field projection (snake→camel), no business logic,
 * no Office.js. Reads `jurisdiction_primary` only — never `family`
 * (country-code clean-cut).
 */

import type { CaseListPort } from '@/domain/case/ports'
import type {
  CaseSummary,
  BriefSummary,
  CaseLanguage,
  CaseFact,
  FactStatus,
  NextEventSummary,
} from '@/domain/case/entity'
import type { VersionSnapshot } from '@/domain/versions/entity'
import type {
  EcritureDetail,
  EcritureSection,
  CitationRef,
  TipTapDoc,
} from '@/domain/ecriture/entity'
import type { HttpErrorKind } from './_errors'

export interface CaseHttpConfig {
  baseUrl: string
  getAccessToken: () => string | null
  fetch?: typeof fetch
}

class CaseHttpError extends Error {
  constructor(
    public readonly kind: HttpErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'CaseHttpError'
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function readString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function readNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function readLanguage(v: unknown): CaseLanguage {
  return v === 'ar' || v === 'en' ? v : 'fr'
}

/** Project the nested `next_event` object, or `null` when absent on the wire.
 *  `days_until` is read straight from the BE — never recomputed (W1). A
 *  missing/non-finite `days_until` yields `null` (no pill) rather than a
 *  misleading "J-0". */
function mapNextEvent(raw: unknown): NextEventSummary | null {
  if (!isPlainObject(raw)) return null
  if (typeof raw.days_until !== 'number' || !Number.isFinite(raw.days_until)) {
    return null
  }
  return {
    title: readString(raw.title),
    daysUntil: raw.days_until,
    importance: readString(raw.importance),
  }
}

function mapCase(raw: unknown): CaseSummary {
  const w = isPlainObject(raw) ? raw : {}
  return {
    id: readString(w.id),
    clientName: readString(w.client_name),
    caseType: readString(w.case_type),
    jurisdictionPrimary: readString(w.jurisdiction_primary),
    status: readString(w.status),
    factCount: readNumber(w.fact_count),
    documentCount: readNumber(w.document_count),
    nextEvent: mapNextEvent(w.next_event),
  }
}

/** Read a nullable string field (snapshot_of / snapshot_label can be null on
 *  the wire). Returns `null` for anything that is not a non-empty string. */
function readNullableString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function mapBrief(raw: unknown): BriefSummary {
  const w = isPlainObject(raw) ? raw : {}
  return {
    id: readString(w.id),
    caseId: readString(w.case_id),
    type: readString(w.type),
    title: readString(w.title),
    status: readString(w.status),
    version: readNumber(w.version),
    language: readLanguage(w.language),
    // Version-history fields (api PR #627 — `snapshot_of` is now on the list
    // wire). `snapshot_of`/`snapshot_label` are nullable; `created_at` is the
    // sort key; `author_name` is BE free-text.
    snapshotOf: readNullableString(w.snapshot_of),
    snapshotLabel: readNullableString(w.snapshot_label),
    authorName: readString(w.author_name),
    createdAt: readString(w.created_at),
  }
}

/** Project a snapshot brief row into the version-history entity. */
function toSnapshot(b: BriefSummary): VersionSnapshot {
  return {
    id: b.id,
    snapshotLabel: b.snapshotLabel,
    authorName: b.authorName,
    createdAt: b.createdAt,
    version: b.version,
  }
}

function readFactStatus(v: unknown): FactStatus {
  return v === 'to_validate' || v === 'established' ? v : 'draft'
}

function mapFact(raw: unknown): CaseFact {
  const w = isPlainObject(raw) ? raw : {}
  return {
    id: readString(w.id),
    text: readString(w.text),
    status: readFactStatus(w.status),
    category: typeof w.category === 'string' ? w.category : null,
  }
}


/** Read a nullable string field, returning `null` for non-strings or empties.
 *  Used for the registry label parts (codeName / articleNumber can be absent
 *  or null on the wire). */
function readNullableNonEmpty(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

/** Project a section's `content_json` (TipTap/ProseMirror doc) verbatim, or
 *  `null` when absent/empty. The BE owns the document schema — the add-in
 *  reads it as-is and renders it read-only (W1); it never validates the legal
 *  node shape here. We only assert the `{type:'doc', content:[...]}` envelope
 *  the renderer walks; anything else (incl. `null`) yields `null`. */
function readContentJson(v: unknown): TipTapDoc | null {
  if (!isPlainObject(v)) return null
  if (v.type !== 'doc') return null
  const content = Array.isArray(v.content) ? v.content : []
  // Cast: nodes are read-only and walked defensively by the renderer.
  return { type: 'doc', content: content as TipTapDoc['content'] }
}

/** Project one `CaseEcritureSectionResponse` row → `EcritureSection`. Reads
 *  `title` / `order` / `content_json` straight off the wire (snake_case; the
 *  ecriture detail is NOT alias-serialised except its nested `registry`). */
function mapSection(raw: unknown): EcritureSection {
  const w = isPlainObject(raw) ? raw : {}
  return {
    id: readString(w.id),
    title: readString(w.title),
    order: readNumber(w.order),
    contentJson: readContentJson(w.content_json),
  }
}

/** Project one registry entry (article OR decision OR chip) → `CitationRef`.
 *  The registry serialises `by_alias=True` so its fields are camelCase
 *  (`refKey`, `codeName`, `articleNumber`). Article entries carry `codeName`;
 *  the unified `chips` slice carries both `codeName` + `articleNumber`. We read
 *  whichever are present — never derive a label the BE didn't ship (W7). */
function mapCitationRef(raw: unknown): CitationRef | null {
  if (!isPlainObject(raw)) return null
  const refKey = readString(raw.refKey)
  if (refKey.length === 0) return null
  return {
    refKey,
    codeName: readNullableNonEmpty(raw.codeName),
    articleNumber: readNullableNonEmpty(raw.articleNumber),
  }
}

/** Build the `{refKey → CitationRef}` lookup the renderer resolves chips from.
 *  Merges the registry's `articles` + `decisions` + `chips` maps (all keyed by
 *  refKey on the wire). A malformed entry is skipped, never fabricated (W7). */
function mapRegistry(raw: unknown): Record<string, CitationRef> {
  // Null-prototype map: the renderer looks refKeys up by indexing this object,
  // so a refKey that collides with an inherited member (`toString`,
  // `constructor`, …) must NOT resolve to a prototype function — that would
  // forge a chip for a citation the BE never shipped (W7). No prototype chain
  // = no such member; it also makes the `in` collision check below exact.
  const out: Record<string, CitationRef> = Object.create(null)
  if (!isPlainObject(raw)) return out
  for (const slice of ['chips', 'articles', 'decisions'] as const) {
    const map = raw[slice]
    if (!isPlainObject(map)) continue
    for (const entry of Object.values(map)) {
      const ref = mapCitationRef(entry)
      // `chips` is read first so its unified shape wins; articles/decisions
      // only fill refKeys a chip descriptor didn't already cover.
      if (ref && !(ref.refKey in out)) out[ref.refKey] = ref
    }
  }
  return out
}

/** Project a `CaseEcritureResponse` → `EcritureDetail`. Sections are sorted by
 *  their BE `order` (stable, ascending) so the preview reads top-to-bottom; the
 *  registry is flattened into a single refKey lookup. Pure projection (W1). */
function mapEcriture(raw: unknown): EcritureDetail {
  const w = isPlainObject(raw) ? raw : {}
  const sections = readItems(w.sections)
    .map(mapSection)
    .sort((a, b) => a.order - b.order)
  return {
    id: readString(w.id),
    title: readString(w.title),
    language: readLanguage(w.language),
    sections,
    registry: mapRegistry(w.registry),
  }
}

/** Tolerate both a bare array and a `{items: [...]}` envelope. */
function readItems(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw
  if (isPlainObject(raw) && Array.isArray(raw.items)) return raw.items
  return []
}

export function createCaseHttp(config: CaseHttpConfig): CaseListPort {
  const doFetch =
    config.fetch ?? ((input, init) => globalThis.fetch(input, init))

  async function get(path: string): Promise<unknown> {
    const token = config.getAccessToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`

    let res: Response
    try {
      res = await doFetch(`${config.baseUrl}${path}`, {
        method: 'GET',
        headers,
        credentials: 'include',
      })
    } catch (err) {
      throw new CaseHttpError(
        'network',
        err instanceof Error ? err.message : 'Réseau injoignable',
      )
    }
    if (res.status === 401) {
      throw new CaseHttpError('auth', 'Session expirée — reconnectez-vous')
    }
    if (res.status === 429) {
      throw new CaseHttpError('rate_limited', 'Trop de requêtes — patientez')
    }
    if (!res.ok) {
      throw new CaseHttpError('server', `Requête échouée (HTTP ${res.status})`)
    }
    try {
      return await res.json()
    } catch (err) {
      throw new CaseHttpError(
        'malformed',
        err instanceof Error ? `JSON invalide: ${err.message}` : 'JSON invalide',
      )
    }
  }

  return {
    async listCases(): Promise<CaseSummary[]> {
      const raw = await get('/api/v1/cases')
      if (!isPlainObject(raw) || !Array.isArray(raw.items)) {
        throw new CaseHttpError('malformed', 'Réponse /cases sans items[]')
      }
      return raw.items.map(mapCase)
    },

    async listBriefs(caseId: string): Promise<BriefSummary[]> {
      const raw = await get(
        `/api/v1/case-ecritures/by-case/${encodeURIComponent(caseId)}`,
      )
      return readItems(raw).map(mapBrief)
    },

    async listFacts(caseId: string): Promise<CaseFact[]> {
      const raw = await get(
        `/api/v1/cases/${encodeURIComponent(caseId)}/facts`,
      )
      return readItems(raw).map(mapFact)
    },

    // Version history is derived from the SAME `by-case` list read as
    // `listBriefs` (no dedicated endpoint): the list includes snapshot rows
    // (BE does not exclude them), so we filter `snapshotOf === briefId` and
    // sort by `createdAt` desc — a pure data projection mirroring the web FE
    // `VersionsPanel.tsx` derivation. W1: filter+sort, no legal logic.
    async listSnapshots(
      caseId: string,
      briefId: string,
    ): Promise<VersionSnapshot[]> {
      const raw = await get(
        `/api/v1/case-ecritures/by-case/${encodeURIComponent(caseId)}`,
      )
      return readItems(raw)
        .map(mapBrief)
        .filter((b) => b.snapshotOf === briefId)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .map(toSnapshot)
    },

    // In-pane read-only version preview (F-VERSIONS-PREVIEW). Fetches one
    // écriture's full detail — ordered sections (TipTap content_json) + the
    // response-attached citation registry — so the renderer can resolve every
    // `citationRef` without a second round-trip (W7). Pure projection (W1).
    async getEcriture(ecritureId: string): Promise<EcritureDetail> {
      const raw = await get(
        `/api/v1/case-ecritures/${encodeURIComponent(ecritureId)}`,
      )
      return mapEcriture(raw)
    },
  }
}

export { CaseHttpError }
